import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';


const skills = [
  {
    name: "Frontend Development",
    percentage: 96,
    tags: ["HTML", "CSS", "JavaScript", "React", "Tailwind CSS"],
    color: "#ff6347" // Tomato Red
  },
  {
    name: "Backend Development",
    percentage: 75,
    tags: ["Node.js", "Express",  "RestAPI", "Sql"],
    color: "#4682b4" // Steel Blue
  },
  {
    name: "Testing",
    percentage: 40,
    tags: ["Selenium", "Mocha", "Swagger", "Postman", "JMeter"],
    color: "#32cd32" // Lime Green
  },
  {
    name: "Cloud Computing",
    percentage: 55,
    tags: ["AWS", "Google Cloud", "DevOps"],
    color: "#ff9900" // Neon Orange
  },
];

const SkillBar = ({ skill, isInView }) => {
  const [currentPercentage, setCurrentPercentage] = useState(0);

  useEffect(() => {
    if (isInView) {
      const timer = setInterval(() => {
        setCurrentPercentage((prev) => {
          if (prev < skill.percentage) {
            return prev + 1;
          }
          clearInterval(timer);
          return prev;
        });
      }, 30);

      return () => clearInterval(timer);
    }
  }, [isInView, skill.percentage]);

  return (
    <motion.div
      className="mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 20 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-medium text-white">{skill.name}</h3>
        <span className="text-sm text-gray-400">{currentPercentage}%</span>
      </div>
      <div className="w-full bg-gray-800 h-4 rounded-full overflow-hidden relative">
        <motion.div
          className="h-full rounded-full"
          style={{
            backgroundColor: skill.color,
            boxShadow: `0 0 10px ${skill.color}, 0 0 20px ${skill.color}, 0 0 30px ${skill.color}`,
          }}
          initial={{ width: 0 }}
          animate={{ width: `${currentPercentage}%` }}
          transition={{ duration: 0.8, ease: "linear" }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {skill.tags.map((tag, index) => (
          <span key={index} className="text-xs text-gray-400">{tag}</span>
        ))}
      </div>
    </motion.div>
  );
};

const SkillsSection = () => {
  const [showIntro, setShowIntro] = useState(false);
  const sectionRef = useRef(null);
  const gifRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.5 });
  
  // Trigger GIF display and the intro only when the section becomes visible
  useEffect(() => {
    if (isInView) {
      setShowIntro(true); // Only start the intro when section is in view
      const timer = setTimeout(() => {
        setShowIntro(false);
      }, 3000); // 3 seconds for the intro animation

      return () => clearTimeout(timer);
    }
  }, [isInView]);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 bg-black min-h-screen relative z-10">
  {/* Background lines */}
  <div className="lines z-0">
    <div className="line"></div>
    <div className="line"></div>
    <div className="line"></div>
  </div>

  <AnimatePresence>
    {showIntro ? (
      <motion.div
        key="intro"
        initial={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
        className="flex justify-center items-center h-[60vh] z-10"
      >
        <div ref={gifRef}>
          <img src="giphy.gif" alt="Skills Intro" className="max-w-full max-h-full" />
        </div>
      </motion.div>
    ) : (
      <motion.div
        key="skills"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeInOut" }}
        className="z-10"
      >
        <h2 className="text-6xl font-bold mb-12 text-center text-white z-10">Skills</h2>
        <div className="max-w-3xl mx-auto z-10">
          {skills.map((skill) => (
            <SkillBar key={skill.name} skill={skill} isInView={isInView} />
          ))}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
</section>
  );
};

export default SkillsSection;
